import { Component, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '../../../../core/services/quiz-management/quiz-management.service';
import { environment } from '../../../../../environments/environment';
import { HasPermissionDirective } from '../../../../access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '../../../../access-role-permissions/users-permissions-constants/student-roles-permissions.enum';

@Component({
  selector: 'app-add-assessment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, HasPermissionDirective],
  templateUrl: './add-assessment-modal.component.html',
  styleUrls: ['./add-assessment-modal.component.scss']
})
export class AddAssessmentModalComponent implements OnInit {
  PermissionForStudents = PermissionForStudents;
  @Input() classroomId!: number;
  private subscription = new Subscription();
  isAdding = false;
  constructor(private router: Router, private route: ActivatedRoute, private quizService: QuizManagementService, private toastr: ToastrService) { }

  @Output() closeModal = new EventEmitter<void>();
  @Input() initialStep: 'choose_type' | 'create_new' | 'choose_existing' | 'select_link' | 'generate_link' = 'choose_type';

  step: 'choose_type' | 'create_new' | 'choose_existing' | 'select_link' | 'generate_link' = 'choose_type';
  selectedAssessment: any = null;

  ngOnInit() {
    this.step = this.initialStep;
  }

  // New Quiz state
  newQuiz = {
    title: '',
    description: '',
    durationHours: 0,
    durationMinutes: 0,
    durationSeconds: 0,
    attempts: 1,
    passingScore: '',
    questionsPerUser: '',
    shuffleQuestions: true,
    shuffleOptions: true,
    enableProctoring: true,
    proctoringOpts: {
      detectTabSwitch: true,
      enableCamera: true,
      multiplePerson: true,
      eyeMovement: true,
      restrictNewTabs: true,
      faceDetection: true,
      noTalking: true
    }
  };

  // Mock existing assessments
  existingAssessments = [
    { name: 'Software Developer — Aptitude + Coding', isRecent: true, questions: 35, duration: '60 min', links: 3, created: 'Apr 5, 2026', status: 'Active' },
    { name: 'Business Analyst — Logical Reasoning', isRecent: false, questions: 25, duration: '45 min', links: 1, created: 'Mar 28, 2026', status: 'Active' },
    { name: 'Data Science Fundamentals', isRecent: true, questions: 40, duration: '75 min', links: 2, created: 'Mar 12, 2026', status: 'Active' },
    { name: 'Marketing Aptitude Round 1', isRecent: false, questions: 20, duration: '30 min', links: 0, created: 'Feb 20, 2026', status: 'Draft' },
    { name: 'DevOps Cloud Basics', isRecent: false, questions: 30, duration: '50 min', links: 4, created: 'Feb 11, 2026', status: 'Active' }
  ];

  existingLinks = [
    { name: 'BBA Campus Hiring', audience: 'Group', invited: 210, start: 'Apr 1, 2026', expiry: 'May 15, 2026', status: 'Active', creator: 'Anika R.' }
  ];

  selectedLinkIndex: number | null = null;
  activeLinkTab = 'active'; // 'active' | 'expired'

  // Generate Link state
  newLink = {
    name: '',
    audienceType: 'specific' as 'specific' | 'grouped',
    emails: '',
    startDate: '2026-05-21T10:04',
    endDate: '',
    expiryHours: '24',
    useDefaultSettings: true
  };

  close() {
    this.closeModal.emit();
  }

  goToStep(newStep: 'choose_type' | 'create_new' | 'choose_existing' | 'select_link' | 'generate_link') {
    this.step = newStep;
  }

  selectExisting(assessment: any) {
    this.selectedAssessment = assessment;
    this.goToStep('select_link');
  }

  selectLink(index: number) {
    this.selectedLinkIndex = index;
  }

  useSelectedLink() {
    if (this.selectedLinkIndex !== null) {
      this.close();
    }
  }

  continueToQuestions() {
    const targetClassroomId = environment.classroomId;
    const jobId = this.route.snapshot.paramMap.get('id') || localStorage.getItem('job_id');
    if (jobId) localStorage.setItem('job_id', jobId);
    const companyId = localStorage.getItem('company_id');

    // Map newQuiz state to CreateQuizPayload
    const payload = {
      quiz_name: this.newQuiz.title,
      description: this.newQuiz.description,
      time_limit: this.newQuiz.durationHours * 3600 + this.newQuiz.durationMinutes * 60 + this.newQuiz.durationSeconds,
      attempts_allowed: this.newQuiz.attempts,
      passing_score: Number(this.newQuiz.passingScore),
      questions_per_quiz: this.newQuiz.questionsPerUser ? Number(this.newQuiz.questionsPerUser) : null,
      mode: 'proctored_assessment',
      shuffle_questions: this.newQuiz.shuffleQuestions,
      shuffle_options: this.newQuiz.shuffleOptions,
      company_id: companyId,
      job_id: jobId,
      proctoring_config: {
        enable_proctoring: this.newQuiz.enableProctoring,
        detect_tab_switch: this.newQuiz.proctoringOpts.detectTabSwitch,
        enable_camera: this.newQuiz.proctoringOpts.enableCamera,
        multiple_person_detection: this.newQuiz.proctoringOpts.multiplePerson,
        eye_movement_tracking: this.newQuiz.proctoringOpts.eyeMovement,
        restrict_new_tabs: this.newQuiz.proctoringOpts.restrictNewTabs,
        face_detection: this.newQuiz.proctoringOpts.faceDetection,
        no_talking_detection: this.newQuiz.proctoringOpts.noTalking,
        max_warnings: 3
      }
    } as any;

    this.isAdding = true;
    this.subscription.add(
      this.quizService.postCreateQuiz(payload, targetClassroomId).pipe(
        tap(response => {
          if (response.Success) {
            this.router.navigate(['/admin-student-platform/create-quiz', targetClassroomId, response.Data.quiz_id]);
            this.close();
          } else {
            this.toastr.error(response?.Message);
          }
        }),
        finalize(() => {
          this.isAdding = false;
        }),
        catchError(() => {
          this.toastr.error('Something went wrong. Please try again');
          return of(null);
        })
      ).subscribe()
    );
  }

  get isQuizFormValid(): boolean {
    const durationInSeconds =
      (this.newQuiz.durationHours || 0) * 3600 +
      (this.newQuiz.durationMinutes || 0) * 60 +
      (this.newQuiz.durationSeconds || 0);

    return !!(
      this.newQuiz.title?.trim() &&
      durationInSeconds > 0 &&
      this.newQuiz.attempts > 0 &&
      this.newQuiz.questionsPerUser &&
      Number(this.newQuiz.questionsPerUser) > 0
    );
  }

}
